import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface VanApplication {
  id: string;
  provider_id: string;
  van_tier: string;
  status: string;
  insurance_status: string;
  weekly_ex_vat: number;
  setup_ex_vat: number;
  setup_paid_at: string | null;
  delivery_date: string | null;
  collection_date: string | null;
  subscription_id: string | null;
  created_at: string;
  users: {
    full_name: string;
    email: string;
    phone: string;
  };
}

interface FleetVan {
  id: string;
  fleet_id: string;
  van_tier: string;
  status: string;
  city: string | null;
  assigned_application_id: string | null;
  notes: string | null;
  created_at: string;
}

interface VanPricing {
  id: string;
  van_tier: string;
  weekly_ex_vat: number;
  setup_ex_vat: number;
  description: string;
  features: string[];
}

interface CalendarEvent {
  id: string;
  type: 'delivery' | 'collection';
  date: Date;
  application: VanApplication;
}

export default function VanFleetManagement() {
  const [activeTab, setActiveTab] = useState<'applications' | 'fleet' | 'calendar' | 'deliveries' | 'payments' | 'collections'>('applications');
  const [applications, setApplications] = useState<VanApplication[]>([]);
  const [fleet, setFleet] = useState<FleetVan[]>([]);
  const [pricing, setPricing] = useState<VanPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddVan, setShowAddVan] = useState(false);
  const [showEditPricing, setShowEditPricing] = useState(false);
  const [selectedApp, setSelectedApp] = useState<VanApplication | null>(null);
  const [selectedVan, setSelectedVan] = useState<FleetVan | null>(null);
  const [newVan, setNewVan] = useState({ fleet_id: '', van_tier: 'small', city: '' });
  const [deliveryDate, setDeliveryDate] = useState('');
  const [collectionDate, setCollectionDate] = useState('');
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [editEventDate, setEditEventDate] = useState('');
  const [editEventTime, setEditEventTime] = useState('');
  const [editEventNotes, setEditEventNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'applications' || activeTab === 'deliveries' || activeTab === 'payments' || activeTab === 'collections' || activeTab === 'calendar') {
        const { data } = await supabase
          .from('van_applications')
          .select(`
            *,
            users:provider_id (
              full_name,
              email,
              phone
            )
          `)
          .order('created_at', { ascending: false });
        
        if (data) setApplications(data);
      }

      if (activeTab === 'fleet') {
        const { data } = await supabase
          .from('fleet_vans')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (data) setFleet(data);
      }

      const { data: pricingData } = await supabase
        .from('van_pricing')
        .select('*')
        .order('weekly_ex_vat', { ascending: true });
      
      if (pricingData) setPricing(pricingData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getCalendarEvents = (): CalendarEvent[] => {
    const events: CalendarEvent[] = [];
    
    applications.forEach(app => {
      if (app.delivery_date) {
        events.push({
          id: `delivery-${app.id}`,
          type: 'delivery',
          date: new Date(app.delivery_date),
          application: app
        });
      }
      if (app.collection_date) {
        events.push({
          id: `collection-${app.id}`,
          type: 'collection',
          date: new Date(app.collection_date),
          application: app
        });
      }
    });
    
    return events;
  };

  const getEventsForDate = (date: Date, events: CalendarEvent[]) => {
    return events.filter(event => 
      event.date.getDate() === date.getDate() &&
      event.date.getMonth() === date.getMonth() &&
      event.date.getFullYear() === date.getFullYear()
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const openEventEditor = (event: CalendarEvent) => {
    setSelectedEvent(event);
    const eventDate = new Date(event.date);
    setEditEventDate(eventDate.toISOString().split('T')[0]);
    setEditEventTime(eventDate.toTimeString().slice(0, 5));
    setEditEventNotes('');
  };

  const saveEventChanges = async () => {
    if (!selectedEvent) return;
    
    const newDateTime = new Date(`${editEventDate}T${editEventTime}`);
    
    try {
      const updateField = selectedEvent.type === 'delivery' ? 'delivery_date' : 'collection_date';
      const { error } = await supabase
        .from('van_applications')
        .update({ 
          [updateField]: newDateTime.toISOString(),
          updated_at: new Date().toISOString() 
        })
        .eq('id', selectedEvent.application.id);

      if (error) throw error;
      
      loadData();
      setSelectedEvent(null);
      alert(`${selectedEvent.type === 'delivery' ? 'Delivery' : 'Collection'} updated successfully`);
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event');
    }
  };

  const deleteEvent = async () => {
    if (!selectedEvent) return;
    
    if (!confirm(`Are you sure you want to remove this ${selectedEvent.type} schedule?`)) return;
    
    try {
      const updateField = selectedEvent.type === 'delivery' ? 'delivery_date' : 'collection_date';
      const statusUpdate = selectedEvent.type === 'delivery' ? 'approved' : 'active';
      
      const { error } = await supabase
        .from('van_applications')
        .update({ 
          [updateField]: null,
          status: statusUpdate,
          updated_at: new Date().toISOString() 
        })
        .eq('id', selectedEvent.application.id);

      if (error) throw error;
      
      loadData();
      setSelectedEvent(null);
      alert(`${selectedEvent.type === 'delivery' ? 'Delivery' : 'Collection'} removed successfully`);
    } catch (error) {
      console.error('Error removing event:', error);
      alert('Failed to remove event');
    }
  };

  const updateApplicationStatus = async (appId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('van_applications')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', appId);

      if (error) throw error;
      loadData();
      alert('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const updateInsuranceStatus = async (appId: string, insuranceStatus: string) => {
    try {
      const { error } = await supabase
        .from('van_applications')
        .update({ insurance_status: insuranceStatus, updated_at: new Date().toISOString() })
        .eq('id', appId);

      if (error) throw error;
      loadData();
      alert('Insurance status updated');
    } catch (error) {
      console.error('Error updating insurance:', error);
      alert('Failed to update insurance status');
    }
  };

  const scheduleDelivery = async (appId: string, date: string) => {
    try {
      const { error } = await supabase
        .from('van_applications')
        .update({ 
          delivery_date: date,
          status: 'scheduled',
          updated_at: new Date().toISOString() 
        })
        .eq('id', appId);

      if (error) throw error;
      loadData();
      setSelectedApp(null);
      alert('Delivery scheduled successfully');
    } catch (error) {
      console.error('Error scheduling delivery:', error);
      alert('Failed to schedule delivery');
    }
  };

  const scheduleCollection = async (appId: string, date: string) => {
    try {
      const { error } = await supabase
        .from('van_applications')
        .update({ 
          collection_date: date,
          status: 'collection_scheduled',
          updated_at: new Date().toISOString() 
        })
        .eq('id', appId);

      if (error) throw error;
      loadData();
      setSelectedApp(null);
      alert('Collection scheduled successfully');
    } catch (error) {
      console.error('Error scheduling collection:', error);
      alert('Failed to schedule collection');
    }
  };

  const updatePricing = async () => {
    try {
      for (const tier of pricing) {
        const { error } = await supabase
          .from('van_pricing')
          .update({ 
            weekly_ex_vat: tier.weekly_ex_vat, 
            setup_ex_vat: tier.setup_ex_vat,
            updated_at: new Date().toISOString()
          })
          .eq('van_tier', tier.van_tier);

        if (error) throw error;
      }
      loadData();
      setShowEditPricing(false);
      alert('Pricing updated successfully');
    } catch (error) {
      console.error('Error updating pricing:', error);
      alert('Failed to update pricing');
    }
  };

  const addFleetVan = async () => {
    if (!newVan.fleet_id || !newVan.van_tier) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('fleet_vans')
        .insert({
          fleet_id: newVan.fleet_id,
          van_tier: newVan.van_tier,
          city: newVan.city || null,
          status: 'available'
        });

      if (error) throw error;
      loadData();
      setShowAddVan(false);
      setNewVan({ fleet_id: '', van_tier: 'small', city: '' });
      alert('Van added to fleet successfully');
    } catch (error) {
      console.error('Error adding van:', error);
      alert('Failed to add van');
    }
  };

  const updateFleetVan = async (vanId: string, updates: Partial<FleetVan>) => {
    try {
      const { error } = await supabase
        .from('fleet_vans')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', vanId);

      if (error) throw error;
      loadData();
      setSelectedVan(null);
      alert('Van updated successfully');
    } catch (error) {
      console.error('Error updating van:', error);
      alert('Failed to update van');
    }
  };

  const deleteFleetVan = async (vanId: string) => {
    if (!confirm('Are you sure you want to delete this van from the fleet?')) return;

    try {
      const { error } = await supabase
        .from('fleet_vans')
        .delete()
        .eq('id', vanId);

      if (error) throw error;
      loadData();
      alert('Van deleted successfully');
    } catch (error) {
      console.error('Error deleting van:', error);
      alert('Failed to delete van');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      submitted: 'bg-blue-50 text-blue-700 border-blue-200',
      approved: 'bg-green-50 text-green-700 border-green-200',
      scheduled: 'bg-purple-50 text-purple-700 border-purple-200',
      active: 'bg-teal-50 text-teal-700 border-teal-200',
      payment_issue: 'bg-red-50 text-red-700 border-red-200',
      overdue: 'bg-orange-50 text-orange-700 border-orange-200',
      collection_scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
      closed: 'bg-gray-50 text-gray-700 border-gray-200',
      available: 'bg-green-50 text-green-700 border-green-200',
      assigned: 'bg-blue-50 text-blue-700 border-blue-200',
      maintenance: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      retired: 'bg-gray-50 text-gray-700 border-gray-200'
    };
    return colors[status] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getStats = () => {
    return {
      totalApplications: applications.length,
      activeVans: applications.filter(a => a.status === 'active').length,
      pendingDeliveries: applications.filter(a => a.status === 'scheduled').length,
      paymentIssues: applications.filter(a => a.status === 'payment_issue').length,
      fleetSize: fleet.length,
      availableVans: fleet.filter(v => v.status === 'available').length
    };
  };

  const stats = getStats();
  const calendarEvents = getCalendarEvents();
  const calendarDays = getDaysInMonth(currentMonth);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Van Fleet Management</h2>
          <p className="text-gray-600 mt-1">Manage van applications, fleet inventory, and operations</p>
        </div>
        <button
          onClick={() => setShowEditPricing(true)}
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-price-tag-3-line"></i>
          Edit Pricing
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-3-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.totalApplications}</p>
              <p className="text-xs text-white/80">Applications</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-truck-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.activeVans}</p>
              <p className="text-xs text-white/80">Active Vans</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-map-pin-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.pendingDeliveries}</p>
              <p className="text-xs text-white/80">Deliveries</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-alert-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.paymentIssues}</p>
              <p className="text-xs text-white/80">Issues</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-car-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.fleetSize}</p>
              <p className="text-xs text-white/80">Fleet Size</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.availableVans}</p>
              <p className="text-xs text-white/80">Available</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6 overflow-x-auto">
          {[
            { id: 'applications', label: 'Applications', icon: 'ri-file-list-3-line', count: applications.length },
            { id: 'fleet', label: 'Fleet Inventory', icon: 'ri-truck-line', count: fleet.length },
            { id: 'calendar', label: 'Calendar', icon: 'ri-calendar-2-line', count: calendarEvents.length },
            { id: 'deliveries', label: 'Deliveries', icon: 'ri-map-pin-line', count: stats.pendingDeliveries },
            { id: 'payments', label: 'Payments', icon: 'ri-bank-card-line', count: stats.activeVans },
            { id: 'collections', label: 'Collections', icon: 'ri-arrow-go-back-line', count: applications.filter(a => ['payment_issue', 'overdue', 'collection_scheduled'].includes(a.status)).length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`pb-3 px-1 border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === tab.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-4">
          {/* Calendar Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Deliveries & Collections Calendar</h3>
                <p className="text-sm text-gray-600 mt-1">View and manage all scheduled events</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-teal-500"></span>
                  <span className="text-sm text-gray-600">Delivery</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                  <span className="text-sm text-gray-600">Collection</span>
                </div>
              </div>
            </div>
            
            {/* Month Navigation */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
              </button>
              <div className="flex items-center gap-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </h4>
                <button
                  onClick={goToToday}
                  className="px-3 py-1 text-sm font-medium text-teal-600 hover:bg-teal-50 rounded-lg transition-colors whitespace-nowrap"
                >
                  Today
                </button>
              </div>
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="p-4">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  const isToday = day && 
                    day.getDate() === new Date().getDate() &&
                    day.getMonth() === new Date().getMonth() &&
                    day.getFullYear() === new Date().getFullYear();
                  
                  const dayEvents = day ? getEventsForDate(day, calendarEvents) : [];
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[100px] border rounded-lg p-2 ${
                        day ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                      } ${isToday ? 'border-teal-500 border-2' : 'border-gray-200'}`}
                    >
                      {day && (
                        <>
                          <div className={`text-sm font-medium mb-1 ${
                            isToday ? 'text-teal-600' : 'text-gray-900'
                          }`}>
                            {day.getDate()}
                          </div>
                          <div className="space-y-1">
                            {dayEvents.slice(0, 3).map(event => (
                              <button
                                key={event.id}
                                onClick={() => openEventEditor(event)}
                                className={`w-full text-left px-2 py-1 rounded text-xs font-medium truncate cursor-pointer transition-all hover:opacity-80 ${
                                  event.type === 'delivery'
                                    ? 'bg-teal-100 text-teal-800 hover:bg-teal-200'
                                    : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                }`}
                              >
                                <i className={`${event.type === 'delivery' ? 'ri-truck-line' : 'ri-arrow-go-back-line'} mr-1`}></i>
                                {event.application.users?.full_name?.split(' ')[0] || 'Unknown'}
                              </button>
                            ))}
                            {dayEvents.length > 3 && (
                              <div className="text-xs text-gray-500 px-2">
                                +{dayEvents.length - 3} more
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Upcoming Events List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Upcoming Events</h3>
              <p className="text-sm text-gray-600 mt-1">Next 7 days</p>
            </div>
            <div className="divide-y divide-gray-200">
              {calendarEvents
                .filter(event => {
                  const now = new Date();
                  const weekFromNow = new Date();
                  weekFromNow.setDate(weekFromNow.getDate() + 7);
                  return event.date >= now && event.date <= weekFromNow;
                })
                .sort((a, b) => a.date.getTime() - b.date.getTime())
                .slice(0, 10)
                .map(event => (
                  <div
                    key={event.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between"
                    onClick={() => openEventEditor(event)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        event.type === 'delivery' ? 'bg-teal-100' : 'bg-amber-100'
                      }`}>
                        <i className={`${
                          event.type === 'delivery' ? 'ri-truck-line text-teal-600' : 'ri-arrow-go-back-line text-amber-600'
                        } text-2xl`}></i>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{event.application.users?.full_name}</p>
                        <p className="text-sm text-gray-500">
                          {event.type === 'delivery' ? 'Van Delivery' : 'Van Collection'} • {event.application.van_tier} van
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {event.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                      <p className="text-sm text-gray-500">
                        {event.date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              {calendarEvents.filter(event => {
                const now = new Date();
                const weekFromNow = new Date();
                weekFromNow.setDate(weekFromNow.getDate() + 7);
                return event.date >= now && event.date <= weekFromNow;
              }).length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <i className="ri-calendar-line text-4xl mb-2"></i>
                  <p>No upcoming events in the next 7 days</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">All Van Applications</h3>
            <p className="text-sm text-gray-600 mt-1">Review and manage provider van applications</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Van Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Insurance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Setup Fee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weekly Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      <i className="ri-inbox-line text-4xl mb-2"></i>
                      <p>No applications found</p>
                    </td>
                  </tr>
                ) : (
                  applications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{app.users?.full_name}</p>
                          <p className="text-sm text-gray-500">{app.users?.email}</p>
                          <p className="text-sm text-gray-500">{app.users?.phone}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="capitalize font-medium text-gray-900">{app.van_tier}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(app.status)}`}>
                          {app.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${
                          app.insurance_status === 'verified' ? 'bg-green-50 text-green-700 border-green-200' :
                          app.insurance_status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-yellow-50 text-yellow-700 border-yellow-200'
                        }`}>
                          {app.insurance_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {app.setup_paid_at ? (
                            <>
                              <i className="ri-checkbox-circle-fill text-green-600 text-lg"></i>
                              <span className="text-sm text-gray-600">£{(app.setup_ex_vat * 1.2).toFixed(2)}</span>
                            </>
                          ) : (
                            <>
                              <i className="ri-close-circle-fill text-red-600 text-lg"></i>
                              <span className="text-sm text-gray-600">£{(app.setup_ex_vat * 1.2).toFixed(2)}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-900">£{(app.weekly_ex_vat * 1.2).toFixed(2)}</span>
                        <span className="text-xs text-gray-500 ml-1">/week</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(app.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedApp(app)}
                          className="text-teal-600 hover:text-teal-700 font-medium text-sm whitespace-nowrap flex items-center gap-1"
                        >
                          <i className="ri-settings-3-line"></i>
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fleet Tab */}
      {activeTab === 'fleet' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddVan(true)}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Add Van to Fleet
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Fleet Inventory</h3>
              <p className="text-sm text-gray-600 mt-1">Manage your van fleet and availability</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fleet ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Van Tier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {fleet.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        <i className="ri-truck-line text-4xl mb-2"></i>
                        <p>No vans in fleet</p>
                      </td>
                    </tr>
                  ) : (
                    fleet.map((van) => (
                      <tr key={van.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-mono text-sm font-medium text-gray-900">{van.fleet_id}</td>
                        <td className="px-6 py-4 capitalize font-medium text-gray-900">{van.van_tier}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(van.status)}`}>
                            {van.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{van.city || '-'}</td>
                        <td className="px-6 py-4">
                          {van.assigned_application_id ? (
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">Assigned</span>
                          ) : (
                            <span className="text-xs bg-gray-50 text-gray-600 px-2 py-1 rounded">Unassigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{van.notes || '-'}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedVan(van)}
                              className="text-teal-600 hover:text-teal-700 font-medium text-sm whitespace-nowrap"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              onClick={() => deleteFleetVan(van.id)}
                              className="text-red-600 hover:text-red-700 font-medium text-sm whitespace-nowrap"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Deliveries Tab */}
      {activeTab === 'deliveries' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Scheduled Deliveries</h3>
            <p className="text-sm text-gray-600 mt-1">Track and manage van deliveries to providers</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Van Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {applications.filter(app => ['approved', 'scheduled'].includes(app.status)).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      <i className="ri-map-pin-line text-4xl mb-2"></i>
                      <p>No pending deliveries</p>
                    </td>
                  </tr>
                ) : (
                  applications.filter(app => ['approved', 'scheduled'].includes(app.status)).map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{app.users?.full_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600">{app.users?.phone}</p>
                        <p className="text-sm text-gray-500">{app.users?.email}</p>
                      </td>
                      <td className="px-6 py-4 capitalize font-medium text-gray-900">{app.van_tier}</td>
                      <td className="px-6 py-4">
                        {app.delivery_date ? (
                          <div>
                            <p className="font-medium text-gray-900">{new Date(app.delivery_date).toLocaleDateString()}</p>
                            <p className="text-xs text-gray-500">{new Date(app.delivery_date).toLocaleTimeString()}</p>
                          </div>
                        ) : (
                          <span className="text-sm text-amber-600 font-medium">Not scheduled</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => {
                            setSelectedApp(app);
                            setDeliveryDate(app.delivery_date || '');
                          }}
                          className="text-teal-600 hover:text-teal-700 font-medium text-sm whitespace-nowrap flex items-center gap-1"
                        >
                          <i className="ri-calendar-line"></i>
                          {app.delivery_date ? 'Reschedule' : 'Schedule'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Active Subscriptions</h3>
            <p className="text-sm text-gray-600 mt-1">Monitor weekly van rental payments</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Van Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weekly Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {applications.filter(app => ['active', 'payment_issue'].includes(app.status)).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      <i className="ri-bank-card-line text-4xl mb-2"></i>
                      <p>No active subscriptions</p>
                    </td>
                  </tr>
                ) : (
                  applications.filter(app => ['active', 'payment_issue'].includes(app.status)).map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{app.users?.full_name}</p>
                        <p className="text-sm text-gray-500">{app.users?.email}</p>
                      </td>
                      <td className="px-6 py-4 capitalize font-medium text-gray-900">{app.van_tier}</td>
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900">£{(app.weekly_ex_vat * 1.2).toFixed(2)}</p>
                        <p className="text-xs text-gray-500">inc VAT</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${
                          app.status === 'payment_issue' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'
                        }`}>
                          {app.status === 'payment_issue' ? 'Issue' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {app.subscription_id ? (
                          <div className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-green-600"></i>
                            <span className="text-sm text-gray-600">Active</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <i className="ri-close-circle-fill text-red-600"></i>
                            <span className="text-sm text-gray-600">Not set up</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedApp(app)}
                          className="text-teal-600 hover:text-teal-700 font-medium text-sm whitespace-nowrap"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Collections Tab */}
      {activeTab === 'collections' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Van Collections</h3>
            <p className="text-sm text-gray-600 mt-1">Manage van returns and collections</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Van Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collection Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {applications.filter(app => ['payment_issue', 'overdue', 'collection_scheduled'].includes(app.status)).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      <i className="ri-arrow-go-back-line text-4xl mb-2"></i>
                      <p>No pending collections</p>
                    </td>
                  </tr>
                ) : (
                  applications.filter(app => ['payment_issue', 'overdue', 'collection_scheduled'].includes(app.status)).map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{app.users?.full_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600">{app.users?.phone}</p>
                        <p className="text-sm text-gray-500">{app.users?.email}</p>
                      </td>
                      <td className="px-6 py-4 capitalize font-medium text-gray-900">{app.van_tier}</td>
                      <td className="px-6 py-4">
                        {app.collection_date ? (
                          <div>
                            <p className="font-medium text-gray-900">{new Date(app.collection_date).toLocaleDateString()}</p>
                            <p className="text-xs text-gray-500">{new Date(app.collection_date).toLocaleTimeString()}</p>
                          </div>
                        ) : (
                          <span className="text-sm text-amber-600 font-medium">Not scheduled</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(app.status)}`}>
                          {app.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedApp(app);
                              setCollectionDate(app.collection_date || '');
                            }}
                            className="text-teal-600 hover:text-teal-700 font-medium text-sm whitespace-nowrap"
                          >
                            <i className="ri-calendar-line"></i>
                          </button>
                          <button
                            onClick={() => updateApplicationStatus(app.id, 'closed')}
                            className="text-green-600 hover:text-green-700 font-medium text-sm whitespace-nowrap"
                          >
                            <i className="ri-checkbox-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Calendar Event Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  selectedEvent.type === 'delivery' ? 'bg-teal-100' : 'bg-amber-100'
                }`}>
                  <i className={`${
                    selectedEvent.type === 'delivery' ? 'ri-truck-line text-teal-600' : 'ri-arrow-go-back-line text-amber-600'
                  } text-2xl`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Edit {selectedEvent.type === 'delivery' ? 'Delivery' : 'Collection'}
                  </h3>
                  <p className="text-sm text-gray-600">{selectedEvent.application.users?.full_name}</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Provider Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Email</span>
                  <span className="text-sm font-medium text-gray-900">{selectedEvent.application.users?.email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Phone</span>
                  <span className="text-sm font-medium text-gray-900">{selectedEvent.application.users?.phone}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Van Tier</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">{selectedEvent.application.van_tier}</span>
                </div>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={editEventDate}
                    onChange={(e) => setEditEventDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                  <input
                    type="time"
                    value={editEventTime}
                    onChange={(e) => setEditEventTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={editEventNotes}
                  onChange={(e) => setEditEventNotes(e.target.value)}
                  rows={3}
                  placeholder="Add any notes about this event..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={deleteEvent}
                className="px-4 py-2 border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>
                Remove
              </button>
              <button
                onClick={() => setSelectedEvent(null)}
                className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={saveEventChanges}
                className="flex-1 px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Pricing Modal */}
      {showEditPricing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Edit Van Pricing</h3>
              <p className="text-sm text-gray-600 mt-1">Update weekly and setup fees for each van tier</p>
            </div>
            <div className="p-6 space-y-4">
              {pricing.map((tier) => (
                <div key={tier.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold text-gray-900 capitalize mb-4 flex items-center gap-2">
                    <i className="ri-truck-line text-teal-600"></i>
                    {tier.van_tier} Van
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Weekly Rate (ex VAT)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          step="0.01"
                          value={tier.weekly_ex_vat}
                          onChange={(e) => {
                            const newPricing = [...pricing];
                            const index = newPricing.findIndex(p => p.id === tier.id);
                            newPricing[index].weekly_ex_vat = parseFloat(e.target.value);
                            setPricing(newPricing);
                          }}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Inc VAT: £{(tier.weekly_ex_vat * 1.2).toFixed(2)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Setup Fee (ex VAT)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          step="0.01"
                          value={tier.setup_ex_vat}
                          onChange={(e) => {
                            const newPricing = [...pricing];
                            const index = newPricing.findIndex(p => p.id === tier.id);
                            newPricing[index].setup_ex_vat = parseFloat(e.target.value);
                            setPricing(newPricing);
                          }}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Inc VAT: £{(tier.setup_ex_vat * 1.2).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowEditPricing(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={updatePricing}
                className="flex-1 px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Van Modal */}
      {showAddVan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Add Van to Fleet</h3>
              <p className="text-sm text-gray-600 mt-1">Register a new van in your fleet inventory</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fleet ID *</label>
                <input
                  type="text"
                  value={newVan.fleet_id}
                  onChange={(e) => setNewVan({ ...newVan, fleet_id: e.target.value })}
                  placeholder="e.g., VAN-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Van Tier *</label>
                <select
                  value={newVan.van_tier}
                  onChange={(e) => setNewVan({ ...newVan, van_tier: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                >
                  <option value="small">Small Van</option>
                  <option value="medium">Medium Van</option>
                  <option value="large">Large Van</option>
                  <option value="luton">Luton Van</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City (Optional)</label>
                <input
                  type="text"
                  value={newVan.city}
                  onChange={(e) => setNewVan({ ...newVan, city: e.target.value })}
                  placeholder="e.g., London"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowAddVan(false);
                  setNewVan({ fleet_id: '', van_tier: 'small', city: '' });
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={addFleetVan}
                className="flex-1 px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Add Van
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Fleet Van Modal */}
      {selectedVan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Edit Fleet Van</h3>
              <p className="text-sm text-gray-600 mt-1">Update van details and status</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fleet ID</label>
                <input
                  type="text"
                  value={selectedVan.fleet_id}
                  onChange={(e) => setSelectedVan({ ...selectedVan, fleet_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={selectedVan.status}
                  onChange={(e) => setSelectedVan({ ...selectedVan, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                >
                  <option value="available">Available</option>
                  <option value="assigned">Assigned</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={selectedVan.city || ''}
                  onChange={(e) => setSelectedVan({ ...selectedVan, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={selectedVan.notes || ''}
                  onChange={(e) => setSelectedVan({ ...selectedVan, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setSelectedVan(null)}
                className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => updateFleetVan(selectedVan.id, {
                  fleet_id: selectedVan.fleet_id,
                  status: selectedVan.status,
                  city: selectedVan.city,
                  notes: selectedVan.notes
                })}
                className="flex-1 px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application Management Modal */}
      {selectedApp && !deliveryDate && !collectionDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Manage Application</h3>
              <p className="text-sm text-gray-600 mt-1">{selectedApp.users?.full_name}</p>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-gray-900">{selectedApp.users?.email}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Phone</p>
                  <p className="font-medium text-gray-900">{selectedApp.users?.phone}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Van Tier</p>
                  <p className="font-medium text-gray-900 capitalize">{selectedApp.van_tier}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Weekly Rate</p>
                  <p className="font-medium text-gray-900">£{(selectedApp.weekly_ex_vat * 1.2).toFixed(2)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Application Status</label>
                  <select
                    value={selectedApp.status}
                    onChange={(e) => updateApplicationStatus(selectedApp.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="submitted">Submitted</option>
                    <option value="approved">Approved</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="active">Active</option>
                    <option value="payment_issue">Payment Issue</option>
                    <option value="overdue">Overdue</option>
                    <option value="collection_scheduled">Collection Scheduled</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Status</label>
                  <select
                    value={selectedApp.insurance_status}
                    onChange={(e) => updateInsuranceStatus(selectedApp.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {selectedApp.delivery_date && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-1">Delivery Scheduled</p>
                  <p className="text-sm text-blue-700">{new Date(selectedApp.delivery_date).toLocaleString()}</p>
                </div>
              )}

              {selectedApp.collection_date && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-sm font-medium text-amber-900 mb-1">Collection Scheduled</p>
                  <p className="text-sm text-amber-700">{new Date(selectedApp.collection_date).toLocaleString()}</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setSelectedApp(null)}
                className="w-full px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Delivery Modal */}
      {selectedApp && deliveryDate !== '' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Schedule Delivery</h3>
              <p className="text-sm text-gray-600 mt-1">{selectedApp.users?.full_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Date & Time</label>
                <input
                  type="datetime-local"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setSelectedApp(null);
                  setDeliveryDate('');
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => scheduleDelivery(selectedApp.id, deliveryDate)}
                className="flex-1 px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Collection Modal */}
      {selectedApp && collectionDate !== '' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Schedule Collection</h3>
              <p className="text-sm text-gray-600 mt-1">{selectedApp.users?.full_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Collection Date & Time</label>
                <input
                  type="datetime-local"
                  value={collectionDate}
                  onChange={(e) => setCollectionDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setSelectedApp(null);
                  setCollectionDate('');
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => scheduleCollection(selectedApp.id, collectionDate)}
                className="flex-1 px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
